import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import {
    getComplementaryItems,
    fetchStoreData,
} from "~/assets/js/Api/storeApi";
import Gallery from "~/assets/js/components/Gallery";
import Header from "~/assets/js/components/Header";
import { DesktopGrid } from "~/assets/js/components/Layout/DesktopGrid";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import i18n from "~/assets/js/Helpers/i18n";
import QuantitySelectButton from "~/assets/js/components/Button/QuantitySelectButton";
import ComplementaryProducts from "~/assets/js/components/ComplementaryProducts";
import { omit } from "lodash";
import Description from "~/assets/js/components/Description";
import ProductInfo from "~/assets/js/components/ProductInfo";
import ProductsProvider, {
    ACTIONS,
    ITEM_TYPE,
    ProductsDetailsContext,
} from "./ProductsProvider";
import { VIEWS } from "./sections/DesktopConfiguratorOverlay";
import {
    DesktopConfiguratorOverlay,
    Nutrients,
    Dressings,
    DesktopBasicIngredients,
    StickyFooter,
    Addons,
} from "./sections";
import useSmoothScroll from "~/assets/js/Helpers/hooks/useSmoothScroll";
import OutOfStockNotification from "./components/Notifications/OutOfStockNotification";
import { DeliveryProvider } from "../../models/DeliveryContext";
import { viewProduct } from "~/assets/js/Api/eventsApi";
import VeganOptions from "./components/VeganOptions";
import GoogleTagUtil from "../../Helpers/google-tag-util";
import { CURRENCY } from "../../Helpers/currency";
import { calculateFranchisePrice } from "~/assets/js/Helpers/util";
import { useApiProduct } from "../../Api/ingredientsApi";
import { MenuPopup } from "./sections/MenuPopup/MenuPopup";
import useFeatureFlags from "../../Helpers/hooks/useFeatureFlags";

const SIMILAR_PRODUCTS_SLUG = "sameCategory";

const ProductDetails = ({ product }) => {
    const { attributes, details } = product;
    const { vegan_type, vegan_related_product } = details;
    const envDetails = omit(details, "recommended_dressings");
    const similarProducts = getComplementaryItems(SIMILAR_PRODUCTS_SLUG);
    const {
        submit,
        isSubmitting,
        state,
        updateProductState,
        fullPrice,
        greenClubMembershipPrice,
        isMainDish,
        isBowl,
        isSalad,
        isSauceProduct,
        inStock,
        isGreenClubMember,
        discountedProductsAvailable,
        storeId,
        selectGreenClubPrice,
        isDiscoverMode,
        triggerAddressPopup,
    } = useContext(ProductsDetailsContext);
    const { isDesktop } = useViewport();
    const [dressingsOpen, setDressingsOpen] = useState(false);
    const [overlayOpen, setOverlayOpen] = useState(null);
    const [footerExtended, setExtended] = useState(false);
    const [menuPopupOpen, setMenuPopupOpen] = useState(false);
    const [menuReminderPopupOpen, setMenuReminderPopupOpen] = useState(false);
    const { greenClubEnabled } = fetchStoreData(storeId);
    const hasAvailableDiscountProducts =
        isGreenClubMember && discountedProductsAvailable > 0;
    const showGreenClubFeature =
        greenClubEnabled &&
        (hasAvailableDiscountProducts || selectGreenClubPrice);
    const { product: apiProduct } = useApiProduct(product.product_id);
    const showNutrition =
        !!details.carbohydrate ||
        !!details.protein ||
        !!details.fat ||
        !!details.sugar ||
        !!details.polyunsaturated_fatty_acid;
    const wasPrice = details.was_prices?.[storeId] ?? details.was_prices?.[0];
    const { FEATURE_MENU } = useFeatureFlags();

    useEffect(() => {
        viewProduct(product.product_id, product.title);
        GoogleTagUtil.trackItemViewEvent({
            currency: CURRENCY.EUR,
            value: parseFloat(calculateFranchisePrice(storeId, product)),
            items: [
                {
                    item_id: String(product.product_id),
                    item_name: product.title,
                    price: parseFloat(
                        calculateFranchisePrice(storeId, product)
                    ),
                    quantity: 1,
                },
            ],
        });
    }, []);

    const updateProductQuantity = (value) => {
        if (isDiscoverMode) {
            triggerAddressPopup();
            return;
        }

        updateProductState({ type: ACTIONS.UPDATE_QUANTITY, payload: value });
    };

    const handleLookIngredientsClick = () => {
        if (isDesktop) {
            return useSmoothScroll("basic-ingredients", 0);
        }

        return setExtended(true);
    };

    const incrementIngredient = (product) => {
        return updateProductState({
            type: ACTIONS.ADD_EXTRA,
            payload: product,
        });
    };

    const decrementIngredient = (product_id) => {
        const data = { product_id, itemType: ITEM_TYPE.EXTRA };

        return updateProductState({ type: ACTIONS.REMOVE, payload: data });
    };

    return (
        <>
            <div style={{ marginBottom: isDesktop ? "64px" : 0 }}>
                {isDesktop && <Header />}
            </div>
            <DesktopGrid
                className="container"
                leftSide={
                    <Gallery
                        images={product.images}
                        backLink={productCategoryLink}
                    />
                }
                rightSide={
                    <div
                        id="pottsalat-product-container"
                        className={isDesktop ? "" : "container"}>
                        <ProductInfo
                            isMainDish={isMainDish}
                            title={product.title}
                            discounted={showGreenClubFeature}
                            price={fullPrice}
                            discountedPrice={greenClubMembershipPrice}
                            taxRate={product.taxRate}
                            attr={product.attributes}
                            details={envDetails}
                            wasPrice={wasPrice}
                        />
                        {!inStock && <OutOfStockNotification />}
                        {!!vegan_type && (
                            <VeganOptions
                                veganType={vegan_type}
                                veganAlternativeUrl={vegan_related_product}
                                isBowl={isBowl}
                                isSalad={isSalad}
                            />
                        )}
                        {isMainDish && product.details.dressing_included && (
                            <Dressings
                                open={dressingsOpen}
                                setOpen={setDressingsOpen}
                            />
                        )}
                        {isMainDish && (
                            <Addons
                                incrementIngredient={incrementIngredient}
                                decrementIngredient={decrementIngredient}
                                product={product}
                                storeId={storeId}
                                handleIngredientsClick={
                                    handleLookIngredientsClick
                                }
                            />
                        )}
                        <QuantitySelectButton
                            onValueChange={updateProductQuantity}
                            quantity={state.quantity}
                            onClick={submit}
                            showMenu={
                                FEATURE_MENU &&
                                apiProduct?.is_menu_item &&
                                isMainDish &&
                                storeId
                            }
                            setMenuPopupOpen={setMenuPopupOpen}
                            menuPopupOpen={menuPopupOpen}
                            setMenuReminderPopupOpen={setMenuReminderPopupOpen}
                            menuReminderPopupOpen={menuReminderPopupOpen}
                            isLoading={isSubmitting}
                            customStyle={
                                inStock || isDiscoverMode ? "" : "disabled"
                            }
                        />
                        <Description desc={product.content} />
                    </div>
                }
            />

            <div className="container product-details-page">
                {isDesktop && isMainDish && (
                    <>
                        <DesktopBasicIngredients
                            ingredientsDescription={
                                details.ingredients_description
                            }
                            setExtrasOpened={() => setOverlayOpen(VIEWS.EXTRA)}
                        />
                        <DesktopConfiguratorOverlay
                            discounted={showGreenClubFeature}
                            open={!!overlayOpen}
                            handleClose={() => setOverlayOpen(null)}
                            initialView={overlayOpen}
                        />
                    </>
                )}
                {showNutrition && (
                    <Nutrients
                        product={product}
                        isMainDish={isMainDish}
                        isSauceProduct={isSauceProduct}
                    />
                )}
                {similarProducts.length > 0 && (
                    <ComplementaryProducts
                        title={i18n.t("complementary.similarProducts")}
                        products={similarProducts}
                    />
                )}
            </div>

            <StickyFooter
                discountedProductsAvailable={discountedProductsAvailable}
                extended={footerExtended}
                setExtended={(toggle) => setExtended(toggle)}
                isMember={isGreenClubMember}
                product={product}
                discounted={showGreenClubFeature}
                selectDressingsAction={() =>
                    isDiscoverMode
                        ? triggerAddressPopup()
                        : setDressingsOpen(true)
                }
                selectGreenClubPrice={selectGreenClubPrice}
            />
        </>
    );
};

const ProductDetailsApp = () => {
    return (
        <DeliveryProvider>
            <ProductsProvider>
                <ProductDetails product={pottsalatProduct} />
            </ProductsProvider>
        </DeliveryProvider>
    );
};

const productType = {
    attributes: PropTypes.arrayOf(PropTypes.shape(attributeType)),
    content: PropTypes.string,
    image: PropTypes.string,
    intolerance: PropTypes.arrayOf(PropTypes.shape(attributeType)),
    priceGross: PropTypes.string,
    priceNet: PropTypes.string,
    taxRate: PropTypes.string,
    title: PropTypes.string,
    details: PropTypes.shape(detailsType),
    ingredients: PropTypes.arrayOf(PropTypes.shape(ingredientType)),
};

const attributeType = {
    id: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string,
};

const detailsType = {
    co2: PropTypes.string,
    energy: PropTypes.string,
    water: PropTypes.string,
    recommended_dressings: PropTypes.number,
};

const ingredientType = {
    id: PropTypes.number,
    name: PropTypes.string,
    images: PropTypes.shape({
        thumb: PropTypes.string,
        normal: PropTypes.string,
    }),
    price: PropTypes.number,
    available: PropTypes.bool,
    energy: PropTypes.string,
    details: PropTypes.shape({
        water: PropTypes.string,
        co2: PropTypes.string,
        infos: PropTypes.string,
    }),
    stock: PropTypes.bool,
    quantity: PropTypes.number,
};

ProductDetails.propTypes = {
    product: PropTypes.shape(productType),
};

export default ProductDetailsApp;
